import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageCircle, 
  Search,
  Filter,
  Clock, 
  User,
  CheckCircle, 
  XCircle,
  Eye,
  X,
  Send,
  Paperclip,
  Star,
  ThumbsUp,
  Reply,
  Edit,
  Trash2,
  AlertCircle,
  BookOpen,
  Calendar,
  Image as ImageIcon
} from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { formatTimestamp } from '../../utils/timestampUtils';
import FileUpload, { useFileUpload } from '../../components/FileUpload';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const AdminDoubtsManagement = () => {
  const { currentUser, userProfile } = useRealtime();
  const [doubts, setDoubts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [selectedDoubt, setSelectedDoubt] = useState(null);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [newAnswer, setNewAnswer] = useState('');
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [doubtToDelete, setDoubtToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // File upload for answer images
  const {
    uploadedFiles: answerImages,
    uploading: uploadingImages,
    progress: uploadProgress,
    uploadFile,
    removeFile,
    setUploadedFiles: setAnswerImages
  } = useFileUpload({ storagePath: 'doubt-answer-images' });

  // Load doubts from Firebase
  useEffect(() => {
    loadDoubtsFromFirebase();
  }, []);

  const loadDoubtsFromFirebase = async () => {
    try {
      setLoading(true);
      const doubtsRef = collection(db, 'doubts');
      const q = query(doubtsRef, orderBy('createdAt', 'desc'));
      const doubtsSnapshot = await getDocs(q);
      const doubtsData = doubtsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setDoubts(doubtsData);
    } catch (error) {
      setError('Failed to load doubts. Please try again later.');
      setDoubts([]);
    } finally {
      setLoading(false);
    }
  };

  // Submit answer to doubt
  const submitAnswer = async () => {
    if (!newAnswer.trim() || !selectedDoubt) {
      toast.error('Please enter an answer');
      return;
    }

    if (!currentUser) {
      toast.error('Please log in to submit an answer');
      return;
    }

    setSubmittingAnswer(true);
    try {
      // Get admin display name
      const adminName = currentUser.displayName || 
                       userProfile?.name || 
                       userProfile?.displayName || 
                       'Admin';

      const answerData = {
        id: Date.now().toString(),
        content: newAnswer,
        author: adminName,
        authorId: currentUser.uid,
        createdAt: new Date().toISOString(),
        isAccepted: false,
        isFromAdmin: true,
        attachments: answerImages || [] // Include uploaded images
      };

      // Add answer to doubt
      const updatedAnswers = [...(selectedDoubt.answers || []), answerData];
      
      // Update doubt status to answered
      await updateDoc(doc(db, 'doubts', selectedDoubt.id), {
        answers: updatedAnswers,
        status: 'answered',
        updatedAt: new Date().toISOString(),
        expert: adminName
      });

      // Update local state
      setDoubts(prev => prev.map(doubt => 
        doubt.id === selectedDoubt.id 
          ? { ...doubt, answers: updatedAnswers, status: 'answered', expert: adminName }
          : doubt
      ));

      setSelectedDoubt(prev => ({ ...prev, answers: updatedAnswers, status: 'answered', expert: adminName }));
      setNewAnswer('');
      setAnswerImages([]); // Clear uploaded images
      setShowAnswerModal(false);
      toast.success('Answer submitted successfully!');
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error(error.message || 'Failed to submit answer. Please try again.');
      setError('Failed to submit answer. Please try again.');
    } finally {
      setSubmittingAnswer(false);
    }
  };

  // Update doubt status
  const updateDoubtStatus = async (doubtId, newStatus) => {
    try {
      await updateDoc(doc(db, 'doubts', doubtId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });

      setDoubts(prev => prev.map(doubt => 
        doubt.id === doubtId 
          ? { ...doubt, status: newStatus, updatedAt: new Date().toISOString() }
          : doubt
      ));

      if (selectedDoubt && selectedDoubt.id === doubtId) {
        setSelectedDoubt(prev => ({ ...prev, status: newStatus, updatedAt: new Date().toISOString() }));
      }
    } catch (error) {
      setError('Failed to update doubt status. Please try again.');
    }
  };

  // Delete doubt and all associated data
  const deleteDoubt = async () => {
    if (!doubtToDelete) return;

    setDeleting(true);
    try {
      // Delete the doubt document (this will cascade delete all associated answers)
      await deleteDoc(doc(db, 'doubts', doubtToDelete.id));

      // Update local state
      setDoubts(prev => prev.filter(doubt => doubt.id !== doubtToDelete.id));
      
      // Close modals
      setShowDeleteModal(false);
      setSelectedDoubt(null);
      setDoubtToDelete(null);
    } catch (error) {
      setError('Failed to delete doubt. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteDoubt = (doubt) => {
    setDoubtToDelete(doubt);
    setShowDeleteModal(true);
  };

  // Filter doubts
  const filteredDoubts = doubts.filter(doubt => {
    const matchesSearch = doubt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doubt.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doubt.author.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || doubt.status === filterStatus;
    const matchesSubject = filterSubject === 'all' || doubt.subject === filterSubject;
    
    return matchesSearch && matchesStatus && matchesSubject;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'answered': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'closed': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'answered': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'closed': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (doubt) => {
    const daysSinceCreated = Math.floor((new Date() - new Date(doubt.createdAt)) / (1000 * 60 * 60 * 24));
    if (daysSinceCreated >= 3) return 'border-l-red-500';
    if (daysSinceCreated >= 1) return 'border-l-yellow-500';
    return 'border-l-green-500';
  };

  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'English', 'Biology', 'Computer Science'];
  const statuses = ['all', 'pending', 'answered', 'closed'];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Doubts Management</h1>
              <p className="text-gray-600 dark:text-gray-400">Manage and answer student doubts</p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">{filteredDoubts.length}</span> total doubts
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-yellow-600">{doubts.filter(d => d.status === 'pending').length}</span> pending
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search doubts, authors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Status</option>
                {statuses.filter(s => s !== 'all').map(status => (
                  <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                ))}
              </select>

              <select
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Subjects</option>
                {subjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Doubts List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDoubts.map((doubt) => (
              <motion.div
                key={doubt.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border-l-4 ${getPriorityColor(doubt)} border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow duration-200`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {doubt.title}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center ${getStatusColor(doubt.status)}`}>
                        {getStatusIcon(doubt.status)}
                        <span className="ml-1">{doubt.status.charAt(0).toUpperCase() + doubt.status.slice(1)}</span>
                      </span>
                      {doubt.status === 'pending' && (
                        <span className="px-2 py-1 text-xs bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 rounded-full flex items-center">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Needs Attention
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                      {doubt.description}
                    </p>
                    
                    {/* Attachments */}
                    {doubt.attachments && doubt.attachments.length > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
                          <Paperclip className="w-4 h-4" />
                          <span>Attachments ({doubt.attachments.length})</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {doubt.attachments.map((attachment, index) => (
                            <div key={index} className="flex items-center space-x-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                              <span className="text-sm text-gray-600 dark:text-gray-300">{attachment.name}</span>
                              <span className="text-xs text-gray-500">({attachment.size})</span>
                              <button
                                onClick={() => window.open(attachment.url, '_blank')}
                                className="text-blue-600 hover:text-blue-800"
                                title="View attachment"
                              >
                                <Eye className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <User className="w-4 h-4" />
                          <span>{doubt.author}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatTimestamp(doubt.createdAt, { includeTime: false, short: true })}</span>
                        </div>
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">
                          {doubt.subject}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span className="text-sm">
                          {doubt.answers ? doubt.answers.length : 0} answer{(doubt.answers ? doubt.answers.length : 0) !== 1 ? 's' : ''}
                        </span>
                        <button
                          onClick={() => setSelectedDoubt(doubt)}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View & Answer
                        </button>
                        <button
                          onClick={() => handleDeleteDoubt(doubt)}
                          className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 flex items-center"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {!loading && filteredDoubts.length === 0 && (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No doubts found</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm || filterStatus !== 'all' || filterSubject !== 'all'
                ? 'Try adjusting your search or filters' 
                : 'No doubts available yet'
              }
            </p>
          </div>
        )}
      </div>

      {/* Doubt Detail & Answer Modal */}
      {selectedDoubt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Doubt Details & Answer</h2>
              <button
                onClick={() => {
                  setSelectedDoubt(null);
                  setNewAnswer('');
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Doubt Header */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <div className="flex items-center space-x-3 mb-3">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {selectedDoubt.title}
                  </h3>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full flex items-center ${getStatusColor(selectedDoubt.status)}`}>
                    {getStatusIcon(selectedDoubt.status)}
                    <span className="ml-1">{selectedDoubt.status.charAt(0).toUpperCase() + selectedDoubt.status.slice(1)}</span>
                  </span>
                </div>
                
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {selectedDoubt.description}
                </p>

                {/* Attachments */}
                {selectedDoubt.attachments && selectedDoubt.attachments.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Attachments</h4>
                    <div className="space-y-2">
                      {selectedDoubt.attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <Paperclip className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">{attachment.name}</p>
                              <p className="text-xs text-gray-500">{attachment.size}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => window.open(attachment.url, '_blank')}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                      <User className="w-4 h-4" />
                      <span>{selectedDoubt.author}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatTimestamp(selectedDoubt.createdAt, { includeTime: false, short: true })}</span>
                    </div>
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                      {selectedDoubt.subject}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status Management */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
                  <div className="flex space-x-2">
                    {['pending', 'answered', 'closed'].map(status => (
                      <button
                        key={status}
                        onClick={() => updateDoubtStatus(selectedDoubt.id, status)}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          selectedDoubt.status === status
                            ? getStatusColor(status)
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteDoubt(selectedDoubt)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center text-sm"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Doubt
                </button>
              </div>

              {/* Answers */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Answers ({selectedDoubt.answers ? selectedDoubt.answers.length : 0})
                </h4>
                
                {selectedDoubt.answers && selectedDoubt.answers.length > 0 ? (
                  <div className="space-y-4">
                    {selectedDoubt.answers.map((answer) => (
                      <div key={answer.id} className={`border rounded-lg p-4 ${
                        answer.isFromAdmin 
                          ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20' 
                          : 'border-gray-200 dark:border-gray-700'
                      }`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900 dark:text-white">{answer.author}</span>
                            {answer.isFromAdmin && (
                              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded-full">
                                Admin
                              </span>
                            )}
                            {answer.isAccepted && (
                              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 rounded-full">
                                Accepted
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500">{formatTimestamp(answer.createdAt, { includeTime: false, short: true })}</span>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mb-3">{answer.content}</p>
                        
                        {/* Answer Attachments/Images */}
                        {answer.attachments && answer.attachments.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <div className="flex flex-wrap gap-2">
                              {answer.attachments.map((attachment, attIndex) => (
                                <div key={attIndex} className="relative group">
                                  {attachment.type?.startsWith('image/') || attachment.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                    <div className="relative">
                                      <img
                                        src={attachment.url}
                                        alt={`Answer image ${attIndex + 1}`}
                                        className="max-w-xs max-h-48 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => window.open(attachment.url, '_blank')}
                                      />
                                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded-lg flex items-center justify-center">
                                        <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                      <Paperclip className="w-4 h-4 text-gray-400" />
                                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[200px]">
                                        {attachment.name || `Attachment ${attIndex + 1}`}
                                      </span>
                                      <button
                                        onClick={() => window.open(attachment.url, '_blank')}
                                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                                        title="View attachment"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">No answers yet. Provide an answer below.</p>
                  </div>
                )}
              </div>

              {/* Answer Form */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Answer</h4>
                <div className="space-y-4">
                  <textarea
                    rows={4}
                    value={newAnswer}
                    onChange={(e) => setNewAnswer(e.target.value)}
                    placeholder="Write your answer here..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                  
                  {/* Image Upload for Answer */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <ImageIcon className="w-4 h-4 inline mr-1" />
                      Attach Images (Optional)
                    </label>
                    <FileUpload
                      onFilesUploaded={(files) => setAnswerImages(files)}
                      multiple={true}
                      maxFiles={5}
                      maxSize={10 * 1024 * 1024} // 10MB
                      storagePath="doubt-answer-images"
                      showPreview={true}
                      showProgress={true}
                      showActions={true}
                      placeholder="Drag and drop images here, or click to select"
                      allowedTypes={['image/*']}
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setSelectedDoubt(null);
                        setNewAnswer('');
                        setAnswerImages([]);
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitAnswer}
                      disabled={(!newAnswer.trim() && (!answerImages || answerImages.length === 0)) || submittingAnswer || uploadingImages}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {submittingAnswer ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit Answer
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && doubtToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Doubt</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDoubtToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to delete this doubt? This action cannot be undone.
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">{doubtToDelete.title}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{doubtToDelete.description}</p>
                <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                  <span>By: {doubtToDelete.author}</span>
                  <span>Subject: {doubtToDelete.subject}</span>
                  <span>Answers: {doubtToDelete.answers ? doubtToDelete.answers.length : 0}</span>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDoubtToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={deleteDoubt}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Doubt
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDoubtsManagement;
